import { Link, router } from '@inertiajs/react'
import { ArrowLeft, Archive } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import type CrmFundraisingRecurringDonation from '@/types/serializers/CrmFundraisingRecurringDonation'
import {
  RECURRING_STATUS_LABELS,
  FREQUENCY_LABELS,
  STATUS_BADGE_VARIANT,
} from '@/lib/fundraising-labels'
import { InstallmentTable } from './_components/InstallmentTable'

interface Props {
  recurring_donation: CrmFundraisingRecurringDonation
  /** When rendered inside a detail sheet: hides full-page chrome and routes
   *  mutations through a partial reload + onMutated callback. */
  inSheet?: boolean
  /** Called after a successful mutation so the sheet can refetch. */
  onMutated?: () => void
  /** Called when the record should no longer be shown in the sheet (e.g. archived). */
  onClose?: () => void
}

export default function RecurringDonationShow({ recurring_donation, inSheet = false, onMutated, onClose }: Props) {
  const rd = recurring_donation

  const status = typeof rd.status === 'object' ? rd.status.value : rd.status
  const statusStr = String(status)

  const frequency = typeof rd.frequency === 'object' ? rd.frequency.value : rd.frequency
  const frequencyStr = String(frequency)

  const isActive = statusStr === 'active' || statusStr === '0'
  const isSuspended = statusStr === 'suspended' || statusStr === '1'
  const isCompleted = statusStr === 'completed' || statusStr === '2'

  const mutationOpts = onMutated
    ? { preserveScroll: true, preserveState: true, onSuccess: () => onMutated() }
    : {}

  function handleTransition(event: string) {
    router.post(`/fundraising/recurring_donations/${rd.id}/transition`, {
      event,
    }, mutationOpts)
  }

  function handleArchive() {
    // In a sheet, archiving removes the row from the kept scope, so refetching
    // the same id would 404 — close the sheet instead of refetching.
    const archiveOpts = onClose
      ? { preserveScroll: true, preserveState: true, onSuccess: () => onClose() }
      : {}
    router.patch(`/fundraising/recurring_donations/${rd.id}/archive`, {}, archiveOpts)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          {!inSheet && (
            <Link
              href="/fundraising/recurring_donations"
              className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
            >
              <ArrowLeft className="mr-1 h-4 w-4" />
              Dons recurrents
            </Link>
          )}
          <h1 className="text-2xl font-bold tracking-tight">
            Don recurrent de {rd.donor_name}
          </h1>
          <div className="flex items-center gap-3">
            <Badge variant={STATUS_BADGE_VARIANT[statusStr] ?? 'outline'}>
              {RECURRING_STATUS_LABELS[statusStr] ?? rd.status.label ?? statusStr}
            </Badge>
            <span className="text-lg font-semibold tabular-nums">
              {rd.amount_per_installment_formatted}
            </span>
            <span className="text-sm text-muted-foreground">
              / {FREQUENCY_LABELS[frequencyStr] ?? rd.frequency.label ?? frequencyStr}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm">
                <Archive className="mr-1.5 h-4 w-4" />
                Archiver
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Archiver ce don recurrent ?</AlertDialogTitle>
                <AlertDialogDescription>
                  Le don recurrent sera archive et ne sera plus visible dans la liste par defaut.
                  Vous pourrez le restaurer a tout moment.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Annuler</AlertDialogCancel>
                <AlertDialogAction onClick={handleArchive}>
                  Archiver
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Transition buttons */}
      {!isCompleted && (
        <div className="flex items-center gap-2">
          {isActive && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => handleTransition('suspend')}
            >
              Suspendre
            </Button>
          )}
          {isSuspended && (
            <Button
              size="sm"
              onClick={() => handleTransition('resume')}
            >
              Reprendre
            </Button>
          )}
          {(isActive || isSuspended) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleTransition('complete')}
            >
              Terminer
            </Button>
          )}
        </div>
      )}

      <Separator />

      {/* Detail cards */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Informations du don recurrent</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Montant par versement</dt>
                <dd className="font-medium tabular-nums">{rd.amount_per_installment_formatted}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Frequence</dt>
                <dd>{FREQUENCY_LABELS[frequencyStr] ?? rd.frequency.label ?? frequencyStr}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Date de debut</dt>
                <dd>{new Date(rd.start_date).toLocaleDateString('fr-FR')}</dd>
              </div>
              {rd.end_date && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">Date de fin</dt>
                  <dd>{new Date(rd.end_date).toLocaleDateString('fr-FR')}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Donateur</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-3 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Nom</dt>
                <dd className="font-medium">{rd.donor_name}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Type</dt>
                <dd>
                  <Badge variant="outline">{rd.donor_type}</Badge>
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Installments table */}
      {rd.installments && (
        <>
          <Separator />
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Versements</h2>
            <InstallmentTable
              installments={rd.installments}
              recurringDonationId={rd.id}
              onMutated={onMutated}
            />
          </div>
        </>
      )}
    </div>
  )
}
